import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  FaArrowRight,
  FaBuilding,
  FaCarSide,
  FaChartLine,
  FaCheckCircle,
  FaClipboardCheck,
  FaCloudSun,
  FaCogs,
  FaExclamationTriangle,
  FaGamepad,
  FaGraduationCap,
  FaHeadset,
  FaPlay,
  FaShieldAlt,
  FaTools,
  FaTruck,
  FaUserTie,
  FaVrCardboard
} from 'react-icons/fa';
import SEO, { siteUrl } from '../components/SEO';

const videoSrc = '/driveskill-vr-demo.mp4';
const thumbnailImage = '/driveskill-vr-thumbnail.jpg';

const featureCards = [
  {
    icon: FaVrCardboard,
    title: 'VR Headset Training',
    description: 'Immersive cockpit practice designed for realistic driving awareness, reactions, and road confidence.'
  },
  {
    icon: FaGamepad,
    title: 'Steering, Pedals & Gear Support',
    description: 'Supports professional wheel, clutch, brake, accelerator, and manual gear-shifter training setups.'
  },
  {
    icon: FaCarSide,
    title: 'Realistic Vehicle Handling',
    description: 'Vehicle physics, dashboard feedback, and driving response built for practical training scenarios.'
  },
  {
    icon: FaCloudSun,
    title: 'Traffic, Weather & Day/Night',
    description: 'AI traffic, dynamic weather, time-of-day variation, maps, routes, and changing driving conditions.'
  },
  {
    icon: FaExclamationTriangle,
    title: 'Hazard Simulation',
    description: 'Practice emergency braking, risky road situations, and hazard response without real-world danger.'
  },
  {
    icon: FaClipboardCheck,
    title: 'Training & Assessment',
    description: 'Assessment modes and driver performance analytics help instructors review progress clearly.'
  }
];

const reasons = [
  'Train drivers in a controlled VR environment before real road exposure',
  'Repeat risky scenarios safely without vehicle damage or public-road risk',
  'Give institutes and organizations a scalable simulator for assessment and practice'
];

const audience = [
  'Driving Schools & Training Institutes',
  'Automotive Companies',
  'Fleet & Logistics Training',
  'Government Transport Departments',
  'Driver Assessment Centers',
  'VR Experience Centers',
  'Road Safety Training Programs'
];

const hardwareSupport = [
  'VR headsets',
  'Steering wheel',
  'Manual gear shifter',
  'H-pattern and sequential shifting',
  'Clutch, accelerator, and brake pedals',
  'Professional driving simulator rigs'
];

const simulatorFeatures = [
  'Real-time VR driving environment',
  'Manual transmission practice',
  'Interactive vehicle dashboard',
  'AI traffic system',
  'Multiple driving maps and routes',
  'Emergency and hazard scenarios',
  'Training and assessment modes',
  'Driver performance analytics'
];

const marketApplications = [
  {
    icon: FaGraduationCap,
    title: 'Driving Schools',
    image: 'https://images.unsplash.com/photo-1503376780353-7e6692767b70?auto=format&fit=crop&w=900&q=80',
    imageAlt: 'Driving training vehicle on an open road',
    description: 'Offer students more practice time, safer first exposure, and premium simulator-based learning packages.'
  },
  {
    icon: FaTruck,
    title: 'Fleet & Logistics',
    image: 'https://images.unsplash.com/photo-1519003722824-194d4455a60c?auto=format&fit=crop&w=900&q=80',
    imageAlt: 'Commercial fleet vehicle training and logistics route',
    description: 'Train drivers for route discipline, hazard response, and consistent operational driving behavior.'
  },
  {
    icon: FaBuilding,
    title: 'Automotive Companies',
    image: 'https://images.unsplash.com/photo-1492144534655-ae79c964c9d7?auto=format&fit=crop&w=900&q=80',
    imageAlt: 'Premium automotive testing and simulation environment',
    description: 'Use VR simulation for demos, behavior testing, internal training, and interactive vehicle experiences.'
  },
  {
    icon: FaUserTie,
    title: 'Assessment Centers',
    image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=900&q=80',
    imageAlt: 'Driver analytics and assessment dashboard',
    description: 'Evaluate driver confidence, reaction quality, gear control, and scenario response in a repeatable setup.'
  }
];

const businessBenefits = [
  'Reduce training cost compared with repeated on-road sessions',
  'Improve driver safety before real vehicle practice',
  'Practice risky and emergency scenarios safely',
  'Deliver a realistic manual transmission experience',
  'Scale training for institutes, fleets, and organizations',
  'Create a premium VR attraction for experience centers'
];

const DriveSkillVR: React.FC = () => {
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);

  const driveSkillJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'VideoGame',
    name: 'DriveSkill VR',
    url: `${siteUrl}/driveskill-vr`,
    image: `${siteUrl}${thumbnailImage}`,
    description: 'DriveSkill VR is a real-time VR driving simulator built by RedRender for Navin Goradara (UK) with steering wheel, pedal, and gear-shifter support for professional training and simulation applications.',
    offers: {
      '@type': 'Offer',
      availability: 'https://schema.org/PreOrder',
      url: `${siteUrl}/contact?service=DriveSkill%20VR%20Demo%20Request`
    }
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-gradient-to-b from-black via-gray-900 to-black pt-24">
      <SEO
        title="DriveSkill VR - Real-Time VR Driving Simulator"
        description="DriveSkill VR is a real-time VR driving simulator built by RedRender for Navin Goradara (UK) featuring steering wheel, gear shifter, pedals, AI traffic, weather, and hazard scenarios."
        path="/driveskill-vr"
        image={thumbnailImage}
        keywords={[
          'DriveSkill VR',
          'VR driving simulator',
          'car driving simulator',
          'steering wheel simulator',
          'manual gear shifter VR',
          'driver training simulator',
          'fleet driver training',
          'Unity VR training',
          'driving practice VR'
        ]}
        jsonLd={driveSkillJsonLd}
      />
      <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.01)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.01)_1px,transparent_1px)] bg-[size:72px_72px] pointer-events-none" />
      <div className="absolute left-[-8rem] top-24 h-72 w-72 rounded-full bg-red-500/20 blur-3xl pointer-events-none" />
      <div className="absolute right-[-6rem] top-[32rem] h-80 w-80 rounded-full bg-red-600/15 blur-3xl pointer-events-none" />
      <div className="absolute bottom-24 left-1/2 h-72 w-72 -translate-x-1/2 rounded-full bg-red-500/10 blur-3xl pointer-events-none" />

      <section className="relative">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20 lg:py-24 relative z-10">
          <div className="grid gap-12 lg:grid-cols-[1.05fr_0.95fr] lg:items-center">
            <div className="animate-slide-up">
              <div className="inline-flex items-center gap-2 rounded-full border border-red-500/30 bg-red-500/10 px-5 py-2 backdrop-blur-sm">
                <span className="relative flex h-2.5 w-2.5">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-500 opacity-75" />
                  <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-red-500" />
                </span>
                <span className="font-display text-sm font-semibold text-red-400">Work In Progress - Demo Requests Open</span>
              </div>

              <h1 className="mt-8 max-w-4xl font-display text-5xl font-bold leading-tight text-white sm:text-6xl lg:text-7xl">
                DriveSkill VR
                <span className="mt-2 block bg-gradient-to-r from-red-500 via-red-300 to-red-500 bg-[length:200%_100%] bg-clip-text text-transparent animate-gradient">
                  Real-Time Driving Simulator with VR Hardware
                </span>
              </h1>

              <p className="mt-6 max-w-2xl text-lg leading-relaxed text-gray-400 sm:text-xl">
                A professional VR driving simulator built by RedRender for Navin Goradara (UK). Designed for training, testing, assessment, and realistic
                manual-transmission practice using headset, steering wheel, pedals, and gear-shifter hardware.
              </p>

              <div className="mt-10 flex flex-col gap-4 sm:flex-row">
                <Link
                  to="/contact?service=DriveSkill%20VR%20Demo%20Request"
                  className="group inline-flex items-center justify-center gap-3 rounded-full border border-red-400/40 bg-gradient-to-r from-red-500 via-red-500 to-red-600 px-8 py-4 font-semibold text-white shadow-[0_0_30px_rgba(239,68,68,0.28)] transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_0_45px_rgba(239,68,68,0.5)]"
                >
                  <span>Request Training Demo</span>
                  <FaArrowRight className="transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
              </div>

              <div className="mt-10 grid max-w-2xl grid-cols-2 gap-4 sm:grid-cols-4">
                {['Unity', 'VR', 'Steering Wheel', 'Gear Shifter', 'AI Traffic', 'Training'].map((item) => (
                  <div
                    key={item}
                    className="rounded-2xl border border-white/10 bg-white/5 px-4 py-4 text-center text-sm font-medium text-gray-300 backdrop-blur-sm"
                  >
                    {item}
                  </div>
                ))}
              </div>
            </div>

            <div className="animate-scale-in">
              <div className="relative overflow-hidden rounded-[2rem] border border-white/10 bg-white/[0.04] p-3 shadow-2xl shadow-red-500/10 backdrop-blur-xl">
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_10%,rgba(239,68,68,0.18),transparent_34%),radial-gradient(circle_at_80%_90%,rgba(220,38,38,0.16),transparent_32%)]" />
                <div className="relative overflow-hidden rounded-[1.5rem] border border-white/10 bg-black">
                  {isVideoLoaded ? (
                    <video
                      src={videoSrc}
                      className="aspect-video w-full bg-black object-cover"
                      controls
                      autoPlay
                      playsInline
                      poster={thumbnailImage}
                      preload="auto"
                    />
                  ) : (
                    <button
                      type="button"
                      onClick={() => setIsVideoLoaded(true)}
                      className="group relative flex aspect-video w-full items-center justify-center overflow-hidden bg-gradient-to-br from-slate-950 via-gray-900 to-black text-left"
                      aria-label="Play DriveSkill VR demo video"
                    >
                      <video
                        src={videoSrc}
                        className="absolute inset-0 h-full w-full object-cover opacity-65 transition-transform duration-700 group-hover:scale-105"
                        muted
                        playsInline
                        preload="metadata"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/25 to-black/20" />
                      <span className="relative flex h-20 w-20 items-center justify-center rounded-full border border-red-200/40 bg-red-500/20 text-white shadow-2xl shadow-red-500/30 backdrop-blur-md transition-all duration-300 group-hover:scale-110 group-hover:bg-red-500/30">
                        <FaPlay className="ml-1 text-2xl" />
                      </span>
                      <span className="absolute bottom-5 left-5 rounded-full border border-white/15 bg-black/65 px-4 py-2 text-sm font-semibold text-white backdrop-blur-md">
                        Play DriveSkill VR Demo
                      </span>
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="relative">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20 relative z-10">
          <div className="grid gap-8 lg:grid-cols-[0.85fr_1.15fr] lg:items-center">
            <div>
              <div className="inline-flex items-center rounded-full border border-red-500/30 bg-red-500/10 px-5 py-2 backdrop-blur-sm">
                <span className="font-display text-sm font-semibold text-red-400">Why DriveSkill VR</span>
              </div>
              <h2 className="mt-6 font-display text-4xl font-bold text-white sm:text-5xl">
                Safer driver training through <span className="text-red-500">realistic simulation</span>
              </h2>
              <p className="mt-5 text-lg leading-relaxed text-gray-400">
                DriveSkill VR gives organizations a safe and cost-effective way to train drivers, test driving behavior,
                and simulate real-world traffic situations without the risk and expense of repeated on-road training.
              </p>
            </div>

            <div className="grid gap-4 sm:grid-cols-3">
              {reasons.map((reason, index) => (
                <div
                  key={reason}
                  className="rounded-3xl border border-white/10 bg-gradient-to-br from-white/5 to-white/0 p-6 backdrop-blur-xl transition-all duration-500 hover:-translate-y-2 hover:border-red-500/40 hover:shadow-2xl hover:shadow-red-500/10"
                >
                  <div className="mb-5 inline-flex h-12 w-12 items-center justify-center rounded-2xl border border-red-500/30 bg-red-500/10 font-display font-bold text-red-400">
                    0{index + 1}
                  </div>
                  <p className="font-semibold leading-relaxed text-white">{reason}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="relative">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20 relative z-10">
          <div className="mb-12 text-center">
            <div className="inline-flex items-center rounded-full border border-red-500/30 bg-red-500/10 px-5 py-2 backdrop-blur-sm">
              <span className="font-display text-sm font-semibold text-red-400">Key Features</span>
            </div>
            <h2 className="mt-6 font-display text-4xl font-bold text-white sm:text-5xl">
              Built for professional <span className="text-red-500">VR driver training</span>
            </h2>
            <p className="mx-auto mt-5 max-w-3xl text-lg leading-relaxed text-gray-400">
              The simulator is designed around real training value: hardware control, road scenarios, repeatable
              practice, and clear assessment for institutes and organizations.
            </p>
          </div>

          <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-4">
            {featureCards.map((feature, index) => (
              <div
                key={feature.title}
                className="group relative overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-br from-white/5 to-white/0 p-8 backdrop-blur-xl transition-all duration-500 hover:-translate-y-2 hover:border-red-500/40 hover:shadow-2xl hover:shadow-red-500/10 animate-scale-in"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="absolute inset-0 bg-gradient-to-br from-red-500/0 to-red-500/0 opacity-0 transition-opacity duration-500 group-hover:from-red-500/10 group-hover:to-transparent group-hover:opacity-100" />
                <feature.icon className="relative text-5xl text-red-500 transition-transform duration-300 group-hover:scale-110 group-hover:rotate-6" />
                <h3 className="relative mt-6 font-display text-2xl font-bold text-white">{feature.title}</h3>
                <p className="relative mt-3 leading-relaxed text-gray-400">{feature.description}</p>
                <div className="absolute right-0 top-0 h-20 w-20 rounded-bl-3xl bg-gradient-to-br from-red-500/20 to-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="relative">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20 relative z-10">
          <div className="grid gap-8 lg:grid-cols-2">
            <div className="rounded-[2rem] border border-white/10 bg-gradient-to-br from-white/10 to-white/0 p-8 shadow-2xl shadow-black/20 backdrop-blur-xl sm:p-10">
              <div className="inline-flex items-center gap-2 rounded-full border border-red-500/30 bg-red-500/10 px-5 py-2">
                <FaHeadset className="text-red-300" />
                <span className="font-display text-sm font-semibold text-red-400">Hardware Support</span>
              </div>
              <h2 className="mt-6 font-display text-3xl font-bold text-white sm:text-4xl">
                Compatible with professional driving setups
              </h2>
              <p className="mt-4 leading-relaxed text-gray-400">
                Designed for a more authentic driving experience using VR and physical driving controls, especially
                for manual transmission practice and serious training environments.
              </p>
              <div className="mt-7 grid gap-4 sm:grid-cols-2">
                {hardwareSupport.map((item) => (
                  <div key={item} className="flex items-center gap-3 rounded-2xl border border-white/10 bg-black/30 p-4 text-gray-200">
                    <FaTools className="flex-shrink-0 text-red-300" />
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-[2rem] border border-white/10 bg-gradient-to-br from-white/10 to-white/0 p-8 shadow-2xl shadow-black/20 backdrop-blur-xl sm:p-10">
              <div className="inline-flex items-center gap-2 rounded-full border border-red-500/30 bg-red-500/10 px-5 py-2">
                <FaCogs className="text-red-300" />
                <span className="font-display text-sm font-semibold text-red-400">Training System</span>
              </div>
              <h2 className="mt-6 font-display text-3xl font-bold text-white sm:text-4xl">
                Feature stack made for training outcomes
              </h2>
              <p className="mt-4 leading-relaxed text-gray-400">
                It is positioned as a practical simulation solution, not only a VR showcase. The focus is repeatable
                practice, measurable improvement, and safer exposure to complex driving conditions.
              </p>
              <div className="mt-7 grid gap-4 sm:grid-cols-2">
                {simulatorFeatures.map((item) => (
                  <div key={item} className="flex items-center gap-3 rounded-2xl border border-white/10 bg-black/30 p-4 text-gray-200">
                    <FaCheckCircle className="flex-shrink-0 text-red-300" />
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="relative">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20 relative z-10">
          <div className="relative overflow-hidden rounded-[2rem] border border-white/10 bg-gradient-to-br from-white/10 to-white/0 p-6 shadow-2xl shadow-black/20 backdrop-blur-xl sm:p-10 lg:p-12">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_25%_20%,rgba(255,85,85,0.20),transparent_30%),radial-gradient(circle_at_75%_80%,rgba(239,68,68,0.15),transparent_34%)]" />
            <div className="relative grid gap-10 lg:grid-cols-2 lg:items-center">
              <div>
                <div className="inline-flex items-center rounded-full border border-red-500/30 bg-red-500/10 px-5 py-2 backdrop-blur-sm">
                  <span className="font-display text-sm font-semibold text-red-400">VR Experience</span>
                </div>
                <h2 className="mt-6 font-display text-4xl font-bold text-white sm:text-5xl">
                  A cockpit-style simulator for real training confidence
                </h2>
                <p className="mt-5 text-lg leading-relaxed text-gray-400">
                  VR presence, dashboard feedback, physical controls, and repeatable road scenarios help learners build
                  road awareness before entering expensive or risky real-world training sessions.
                </p>
              </div>

              <div className="relative min-h-[320px] rounded-[2rem] border border-red-500/20 bg-black/40 p-8">
                <div className="absolute inset-x-8 top-10 h-px bg-gradient-to-r from-transparent via-red-300/60 to-transparent" />
                <div className="absolute left-8 top-16 h-24 w-24 rounded-full border border-red-500/40 bg-red-500/10" />
                <div className="absolute right-8 top-16 h-24 w-24 rounded-full border border-red-500/40 bg-red-500/10" />
                <div className="absolute inset-x-[18%] bottom-12 h-28 rounded-t-full border-t border-x border-red-500/35 bg-red-500/10" />
                <div className="absolute bottom-20 left-1/2 h-24 w-24 -translate-x-1/2 rounded-full border-[10px] border-red-500/70 bg-black/60" />
                <div className="absolute left-1/2 top-1/2 flex -translate-x-1/2 -translate-y-1/2 items-center gap-3 rounded-full border border-white/10 bg-white/5 px-5 py-3 text-sm font-semibold text-white backdrop-blur-md">
                  <FaVrCardboard className="text-red-400" />
                  VR Ready
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="relative">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20 relative z-10">
          <div className="mb-12 max-w-4xl">
            <div className="inline-flex items-center gap-2 rounded-full border border-red-500/30 bg-red-500/10 px-5 py-2 backdrop-blur-sm">
              <FaChartLine className="text-red-300" />
              <span className="font-display text-sm font-semibold text-red-400">Market Value & Applications</span>
            </div>
            <h2 className="mt-6 font-display text-4xl font-bold text-white sm:text-5xl">
              A simulator businesses can sell, deploy, and scale
            </h2>
            <p className="mt-5 text-lg leading-relaxed text-gray-400">
              DriveSkill VR is useful for organizations that need safer driver preparation, better assessment, and a
              modern training experience without depending only on road vehicles, fuel, instructors, and physical risk.
            </p>
          </div>

          <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-4">
            {marketApplications.map((item, index) => (
              <div
                key={item.title}
                className="group overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-br from-white/5 to-white/0 backdrop-blur-xl transition-all duration-500 hover:-translate-y-2 hover:border-red-500/40 hover:shadow-2xl hover:shadow-red-500/10 animate-scale-in"
                style={{ animationDelay: `${index * 0.08}s` }}
              >
                <div className="relative h-44 overflow-hidden">
                  <img
                    src={item.image}
                    alt={item.imageAlt}
                    className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110"
                    loading="lazy"
                    onError={(event) => {
                      event.currentTarget.src = thumbnailImage;
                    }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/35 to-transparent" />
                  <div className="absolute left-5 top-5 inline-flex h-12 w-12 items-center justify-center rounded-2xl border border-red-200/30 bg-black/50 text-red-300 shadow-xl shadow-black/30 backdrop-blur-md">
                    <item.icon className="text-xl transition-transform duration-300 group-hover:scale-110" />
                  </div>
                  <div className="absolute bottom-5 left-5 right-5">
                    <h3 className="font-display text-xl font-bold text-white">{item.title}</h3>
                    <p className="mt-1 text-xs font-semibold uppercase tracking-[0.18em] text-red-200">
                      Training market
                    </p>
                  </div>
                </div>

                <div className="p-6">
                  <p className="leading-relaxed text-gray-300">{item.description}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-10 rounded-[2rem] border border-white/10 bg-gradient-to-br from-red-500/10 via-white/[0.04] to-white/[0.02] p-8 backdrop-blur-xl sm:p-10">
            <div className="grid gap-8 lg:grid-cols-[0.8fr_1.2fr] lg:items-start">
              <div>
                <div className="inline-flex items-center gap-2 rounded-full border border-red-500/30 bg-red-500/10 px-5 py-2">
                  <FaShieldAlt className="text-red-300" />
                  <span className="font-display text-sm font-semibold text-red-400">Business Benefits</span>
                </div>
                <h3 className="mt-6 font-display text-3xl font-bold text-white sm:text-4xl">
                  Why organizations should use it
                </h3>
                <p className="mt-4 leading-relaxed text-gray-400">
                  It turns training into a repeatable product: safer practice, measurable driver improvement, lower
                  operating cost, and a premium VR experience for institutes or enterprise clients.
                </p>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                {businessBenefits.map((item) => (
                  <div key={item} className="flex items-start gap-3 rounded-2xl border border-white/10 bg-black/25 p-4 text-gray-200">
                    <FaCheckCircle className="mt-1 flex-shrink-0 text-red-300" />
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="relative">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20 relative z-10">
          <div className="mb-12 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <div className="inline-flex items-center rounded-full border border-red-500/30 bg-red-500/10 px-5 py-2 backdrop-blur-sm">
                <span className="font-display text-sm font-semibold text-red-400">Who It Is For</span>
              </div>
              <h2 className="mt-6 font-display text-4xl font-bold text-white sm:text-5xl">
                Made for professional <span className="text-red-500">training markets</span>
              </h2>
            </div>
            <p className="max-w-xl text-base leading-relaxed text-gray-400 sm:text-lg">
              A practical VR simulator for institutions, enterprises, and experience centers that need safer driver
              training, better assessment, and a modern interactive training product.
            </p>
          </div>

          <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-4">
            {audience.map((item) => (
              <div
                key={item}
                className="rounded-3xl border border-white/10 bg-gradient-to-br from-white/5 to-white/0 p-7 backdrop-blur-xl transition-all duration-500 hover:-translate-y-2 hover:border-red-500/40 hover:shadow-2xl hover:shadow-red-500/10"
              >
                <div className="mb-6 h-1.5 w-20 rounded-full bg-gradient-to-r from-red-500 to-red-300" />
                <h3 className="font-display text-2xl font-bold text-white">{item}</h3>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="relative">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20 pb-20 relative z-10">
          <div className="relative overflow-hidden rounded-[2rem] border border-white/10 bg-gradient-to-br from-white/10 to-white/0 px-6 py-12 text-center shadow-2xl shadow-black/20 backdrop-blur-xl sm:px-10 sm:py-16">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,_rgba(255,85,85,0.22),_transparent_55%)]" />
            <div className="absolute -top-12 left-1/2 h-32 w-32 -translate-x-1/2 rounded-full bg-red-500/20 blur-3xl" />
            <div className="relative">
              <h2 className="font-display text-3xl font-bold text-white sm:text-4xl lg:text-5xl">
                Watch DriveSkill VR in action
              </h2>
              <p className="mx-auto mt-5 max-w-2xl text-lg leading-relaxed text-gray-400">
                The simulator is still in development, but demo requests are open for training institutes,
                organizations, and VR experience businesses exploring serious driver simulation.
              </p>
              <Link
                to="/contact?service=DriveSkill%20VR%20Demo%20Request"
                className="group mt-8 inline-flex items-center gap-3 rounded-full border border-red-400/40 bg-gradient-to-r from-red-500 via-red-500 to-red-600 px-8 py-4 font-semibold text-white shadow-[0_0_30px_rgba(239,68,68,0.28)] transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_0_45px_rgba(239,68,68,0.5)]"
              >
                <span>Request Training Demo</span>
                <FaArrowRight className="transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default DriveSkillVR;
